import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Send, Check, Loader2, AlertTriangle } from "lucide-react";

import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Input";
import {
  broadcastToEventTelegram,
  fetchEventRosterForOrganizer,
  type EventRoster,
} from "@/lib/api/broadcast";

interface Props {
  eventId: string;
}

const MAX_MESSAGE = 3500;

/**
 * "Notify participants via Telegram" button for the organizer dashboard.
 * Opens a modal with:
 *   - Roster of participants (checkbox per row).
 *   - "Select all reachable" shortcut.
 *   - Textarea for the broadcast body with a live char counter.
 *   - Send / Cancel.
 *
 * Participants without a linked Telegram are listed but disabled (with
 * a quiet "no Telegram" hint) — that way the organizer can see who's
 * missing and chase them by other means.
 */
export function TelegramBroadcastButton({ eventId }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setOpen(true)}
        leadingIcon={<Send className="h-4 w-4" />}
      >
        Notify via Telegram
      </Button>
      {open ? (
        <BroadcastModal eventId={eventId} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

function BroadcastModal({
  eventId,
  onClose,
}: {
  eventId: string;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    sent: number;
    failed: number;
    skipped: number;
  } | null>(null);

  const { data: roster = [], isLoading } = useQuery({
    queryKey: ["broadcast-roster", eventId],
    queryFn: () => fetchEventRosterForOrganizer(eventId),
  });

  const reachable = useMemo(
    () => roster.filter((r) => r.hasTelegram),
    [roster],
  );
  const unreachable = useMemo(
    () => roster.filter((r) => !r.hasTelegram),
    [roster],
  );

  function toggle(participantId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(participantId)) next.delete(participantId);
      else next.add(participantId);
      return next;
    });
  }

  function selectAllReachable() {
    setSelected(new Set(reachable.map((r) => r.participantId)));
  }
  function deselectAll() {
    setSelected(new Set());
  }

  async function handleSend() {
    setError(null);
    setSending(true);
    try {
      const data = await broadcastToEventTelegram({
        eventId,
        participantIds: Array.from(selected),
        message: message.trim(),
      });
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't send the broadcast.");
    } finally {
      setSending(false);
    }
  }

  const trimmedLen = message.trim().length;
  const canSend = !sending && selected.size > 0 && trimmedLen > 0 && trimmedLen <= MAX_MESSAGE;

  // After a successful send the modal flips to a "Sent" confirmation
  // screen — no double-send race, clearer feedback than a toast.
  if (result) {
    return (
      <Modal open onClose={onClose} title="Sent">
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-[14px] font-semibold text-[#15803d]">
            <Check className="h-4 w-4" />
            Telegram messages dispatched.
          </p>
          <ul className="space-y-1 text-[13px] text-[#475569]">
            <li>
              <span className="font-semibold text-[#16a34a]">{result.sent}</span>{" "}
              delivered
            </li>
            {result.failed > 0 ? (
              <li>
                <span className="font-semibold text-[#b91c1c]">{result.failed}</span>{" "}
                failed at Telegram's end (rare — try again later)
              </li>
            ) : null}
            {result.skipped > 0 ? (
              <li>
                <span className="font-semibold text-[#94a3b8]">{result.skipped}</span>{" "}
                skipped (Telegram not linked)
              </li>
            ) : null}
          </ul>
          <Button onClick={onClose} fullWidth>
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title="Notify participants via Telegram">
      <div className="space-y-4">
        <p className="text-[13px] text-[#6c757d]">
          Pick who should get the message, write a short note, and send. Only
          participants who connected Telegram are reachable from here — the
          others stay in the list as a reminder.
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-[#6c757d]">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading roster…
          </div>
        ) : roster.length === 0 ? (
          <p className="text-[13px] text-[#6c757d]">No participants yet.</p>
        ) : (
          <>
            {/* Recipients header + bulk actions */}
            <div className="flex items-center justify-between border-b border-[#e2e8f0] pb-2">
              <p className="text-[12px] font-bold uppercase tracking-wide text-[#94a3b8]">
                Recipients · {selected.size} selected
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllReachable}
                  disabled={reachable.length === 0}
                  className="text-[12px] font-semibold text-[#2563eb] hover:underline disabled:text-[#cbd5e1] disabled:no-underline"
                >
                  Select all reachable
                </button>
                {selected.size > 0 ? (
                  <button
                    type="button"
                    onClick={deselectAll}
                    className="text-[12px] font-semibold text-[#6c757d] hover:underline"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>

            <div className="max-h-[280px] space-y-1 overflow-y-auto pr-1">
              {reachable.map((p) => (
                <RecipientRow
                  key={p.participantId}
                  participant={p}
                  selected={selected.has(p.participantId)}
                  onToggle={() => toggle(p.participantId)}
                />
              ))}
              {unreachable.length > 0 ? (
                <p className="mt-3 pt-2 text-[11px] font-bold uppercase tracking-wide text-[#94a3b8] border-t border-[#e2e8f0]">
                  No Telegram linked · cannot reach
                </p>
              ) : null}
              {unreachable.map((p) => (
                <RecipientRow
                  key={p.participantId}
                  participant={p}
                  selected={false}
                  onToggle={() => {}}
                  disabled
                />
              ))}
            </div>

            <div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="Hey everyone — quick reminder, doors open at 18:30 sharp. Bring your ID."
                maxLength={MAX_MESSAGE + 100}
              />
              <div className="mt-1 flex items-center justify-between text-[11px] text-[#94a3b8]">
                <span>
                  Bot adds a header / footer automatically. Keep it short and personal.
                </span>
                <span className={trimmedLen > MAX_MESSAGE ? "text-[#b91c1c]" : ""}>
                  {trimmedLen} / {MAX_MESSAGE}
                </span>
              </div>
            </div>

            {error ? (
              <div className="flex items-start gap-2 rounded-md border border-[#fecaca] bg-[#fef2f2] p-2 text-[12px] text-[#991b1b]">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}

            <div className="flex justify-end gap-2 border-t border-[#e2e8f0] pt-3">
              <Button variant="secondary" onClick={onClose} disabled={sending}>
                Cancel
              </Button>
              <Button onClick={handleSend} disabled={!canSend} loading={sending}>
                {sending
                  ? "Sending…"
                  : `Send to ${selected.size} ${selected.size === 1 ? "person" : "people"}`}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function RecipientRow({
  participant,
  selected,
  onToggle,
  disabled,
}: {
  participant: EventRoster;
  selected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  const subtitle = [participant.role, participant.company].filter(Boolean).join(" · ");
  return (
    <label
      className={`flex items-center gap-3 rounded-[8px] px-2 py-1.5 ${
        disabled ? "opacity-50" : "cursor-pointer hover:bg-[#f8fafc]"
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        disabled={disabled}
        className="h-4 w-4 rounded border-[#cbd5e1] text-[#2563eb] focus:ring-[#2563eb] disabled:opacity-50"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-[#1a1a1a]">
          {participant.name}
          {participant.hasTelegram && participant.telegramUsername ? (
            <span className="ml-1.5 text-[11px] font-normal text-[#94a3b8]">
              @{participant.telegramUsername}
            </span>
          ) : null}
        </p>
        {subtitle ? (
          <p className="truncate text-[11px] text-[#6c757d]">{subtitle}</p>
        ) : null}
      </div>
      {disabled ? (
        <span className="text-[10px] uppercase tracking-wide text-[#94a3b8]">
          no Telegram
        </span>
      ) : null}
    </label>
  );
}
